import React from 'react';
import {
  SearchModule,
  AutocompleteModule,
  TagModule,
  ActionModule,
  ClipboardModule,
  isNoCommaString,
  QueryScore,
} from './Modules';
import { ResultDescription, ResultHead } from '../Expression/ExpressionSearch';
import { SessionContext } from '@benzinga/session-context';
import { SearchContext, SearchContextType } from '../context';
import styled from 'styled-components';
import { VerticalsManager } from '@benzinga/verticals-manager';
import {
  SearchItem,
  VerticalModuleId,
  VerticalSearchItemData,
  VerticalSearchItemVersion,
  createVerticalSearchItem,
  isVerticalSearchItem,
} from '@benzinga/search-modules';
import { FaLayerGroup } from 'react-icons/fa';
import { WidgetContext, WidgetId } from '@benzinga/widget-tools';
import Styles from '../styles/styles.module.scss';

export interface VerticalSearchModule extends SearchModule<VerticalSearchItemData> {
  id: typeof VerticalModuleId;
  autocomplete: AutocompleteModule<VerticalSearchItemData>;
}

const VerticalDropdown: (label: string) => React.FC<{ isSelected: boolean; isLocked?: boolean }> =
  (label: string) => props => {
    return (
      <>
        <ResultHead $isSelected={props.isSelected} className={Styles.resultHead}>
          {'Verticals of'}
        </ResultHead>
        <ResultDescription $isSelected={props.isSelected} className={Styles.resultDescription}>
          {label}
        </ResultDescription>
      </>
    );
  };

export const createVerticalAutocompleteModule = (
  verticalsManager: VerticalsManager,
  searchContext: SearchContextType,
  widgetId?: WidgetId,
): AutocompleteModule<VerticalSearchItemData> => {
  return {
    dropdown: (item: SearchItem) => {
      if (!isVerticalSearchItem(item)) return undefined;
      const vertical = verticalsManager.getCachedVerticalByVerticalName(item.data.verticalName);

      return {
        component: VerticalDropdown(vertical?.verticalName ?? 'VERTICAL NOT FOUND'),
        id: item.id,
        locked: false,
        searchItem: item,
        type: 'AutocompleteDropdown',
        version: VerticalSearchItemVersion,
      };
    },
    onSelect: (item: SearchItem) => {
      if (!isVerticalSearchItem(item)) return undefined;
      return item;
    },
    query: (text: string, limit = 10) => {
      const verticals = verticalsManager.getVerticalsCached();
      const recentlyUsed = searchContext
        .getRecentModuleItem(widgetId ?? '')
        ?.filter(isVerticalSearchItem)
        .map<[SearchItem<VerticalSearchItemData>, QueryScore]>(item => [item, QueryScore.Priority]);
      const verticalItems = verticals.map<[SearchItem<VerticalSearchItemData>, QueryScore]>(vertical => [
        createVerticalSearchItem(vertical.verticalName),
        QueryScore.NoMatch,
      ]);
      const uniqueVerticals = new Map<string, [SearchItem<VerticalSearchItemData>, QueryScore]>();
      [...recentlyUsed, ...verticalItems].forEach(item => {
        const name = item?.[0]?.data.verticalName;
        if (!uniqueVerticals.has(name)) {
          uniqueVerticals.set(name, item);
        }
      });
      let results = Array.from(uniqueVerticals.values());
      if (text) {
        const lower = text.toLowerCase();
        results = results.filter(([item]) => item.data.verticalName.toLowerCase().includes(lower));
      }
      return {
        cancel: () => [],
        promise: Promise.resolve({
          canceled: false,
          ok: results.slice(0, limit),
        }),
      };
    },
  };
};

export const createVerticalClipboardModule = (): ClipboardModule => {
  return {
    onCopyJSON: token => {
      if (!isVerticalSearchItem(token)) return undefined;
      return [token];
    },
    onCopyString: token => {
      if (!isVerticalSearchItem(token)) return undefined;
      return isNoCommaString(token.data.verticalName) ? [token.data.verticalName] : undefined;
    },
    onPasteJSON: () => {
      return undefined;
    },
  };
};

export const createVerticalTagModule = (): TagModule => {
  return {
    render: item => {
      if (!isVerticalSearchItem(item)) return undefined;
      return {
        icon: <StyledVerticalIcon />,
        text: item.data.verticalName,
        type: 'text',
      };
    },
    tagInputText: item => {
      if (!isVerticalSearchItem(item)) return undefined;
      return item.data.verticalName;
    },
  };
};

const VerticalModuleItemLoader = (item: SearchItem) => {
  switch (item.version) {
    case VerticalSearchItemVersion:
    default:
      return item as SearchItem<VerticalSearchItemData>;
  }
};

export const createVerticalActionModule = (): ActionModule => {
  return {
    dropdown: () => {
      return [];
    },
  };
};

export const createVerticalSearchModule = (
  verticalsManager: VerticalsManager,
  search: SearchContextType,
  widgetId?: WidgetId,
): VerticalSearchModule => {
  return {
    action: createVerticalActionModule(),
    autocomplete: createVerticalAutocompleteModule(verticalsManager, search, widgetId),
    clipboard: createVerticalClipboardModule(),
    id: VerticalModuleId,
    loader: VerticalModuleItemLoader,
    tag: createVerticalTagModule(),
  };
};

export const useVerticalSearchModule = () => {
  const session = React.useContext(SessionContext);
  const search = React.useContext(SearchContext);
  const verticalsManager = session.getManager(VerticalsManager);
  const widgetContext = React.useContext(WidgetContext);
  return React.useMemo(
    () => createVerticalSearchModule(verticalsManager, search, widgetContext.widgetId),
    [verticalsManager, search, widgetContext.widgetId],
  );
};

const StyledVerticalIcon = styled(FaLayerGroup)`
  padding-top: 3px;
`;
